import {Formik} from 'formik'
import {useEffect, useRef, useState} from 'react'
import {SimpleBlueButton} from '../../components/core/button'
import CustomSelect from '../../components/core/customSelect'
import Checkbox from '../../components/core/checkbox'
import {ArrowDownIcon, ArrowUpIcon, DividerIcon, HelpIcon} from '../../icons'
import {useDispatch, useSelector} from 'react-redux'
import Tooltip from '../../components/core/tooltip'
import {colors} from '../../utils/colors'
import CustomCalendar from '../../components/core/calendar'
import {scheduleSchema} from '../../utils/validation'
import CustomTimePicker from '../../components/core/timePicker'
import {combine, DateFormats, formatDate, formatTimeString, normalize} from '../../utils/date'
import {createSchedule, getAllMembersSchedule, updateSchedule} from '../../stores/incident-management'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'

const ALL = 'all'
const MON_FRI = 'mon-fri'
const NEVER = 'never'
const ON = 'on'
const SATURDAY = 'Sa'
const SUNDAY = 'S'
const DEFAULT_SECOND = '00'

const names = {
    start_time: 'start_time',
    end_time: 'end_time',
    team_member:'team_member',
    weekdays_range:'weekdays_range',
    start_date: 'start_date',
    end_date: 'end_date',
    months: 'months',
    weekdays: 'weekdays',
    end_date_available: 'end_date_available'
}

const monFriDays = [1, 2, 3, 4, 5]

const DEFAULT_START_HOUR = '00:00'
const DEFAULT_END_HOUR = '23:59'

const CreateEditSchedule = ({handleModal, members, isEdit, updatedSchedule, getFormattedTimeInTimeZone, isTimeRangeExceeded, hasBackOption, isOnCallEnabled}) => {
    const dispatch = useDispatch()
    const text = useNamespaceTranslations('on-call-scheduling')
    const formikRef = useRef(null)
    const containerRef = useRef(null)
    const [isButtonLoading, setIsButtonLoading] = useState(false)
    const [active, setActive] = useState(null)
    const [isAdvanceShowed, setIsAdvanceShowed] = useState(false)
    const [selectedWeeks, setSelectedWeeks] = useState([])
    const [selectedWeekdays, setSelectedWeekdays] = useState([])
    const [selectedMonths, setSelectedMonths] = useState(text.months.map(month => ({...month, isChecked: true})))
    const [daysRange, setDaysRange] = useState(null)
    const [dateStatus, setDateStatus] = useState(NEVER)
    const currentDate = formatDate(new Date(), DateFormats.DOT_DMY)
    const currentEndDate = '31.12.9999'
    const currentTeamId = useSelector(state => state.teams.selectedTeamId)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)

    const membersOptions = members && !!members.length && members?.map(member => ({
        id: member.customer_id,
        label: `${member.name} ${member.surname}`,
        value: `${member.name} ${member.surname}`
    })) || []

    const triggerAdvanced = () => isOnCallEnabled && setIsAdvanceShowed(prev => !prev)

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const handleWeeks = (week) => {
        setSelectedWeeks((prevSelectedWeeks) =>
            prevSelectedWeeks.map((day) =>
                day.value === week ? {...day, selected: !day.selected} : day
            )
        )
    }

    const handleWeekdays = (selectedDay, setFieldError) => {
        let updatedSelectedWeekdays

        setFieldError(names.weekdays, null)
        setDaysRange(selectedDay)

        if (selectedDay === ALL) {
            updatedSelectedWeekdays = text.weekdays.map((weekday, index) => ({day: weekday.label, value: index, selected: true}))
            setSelectedWeekdays(updatedSelectedWeekdays)
            return
        }

        if (selectedDay === MON_FRI) {
            updatedSelectedWeekdays = selectedWeekdays.filter(weekday => weekday.day !== SATURDAY && weekday.day !== SUNDAY).map(weekday => ({...weekday, selected: true}))
            setSelectedWeekdays(updatedSelectedWeekdays)
            return
        }

        updatedSelectedWeekdays = selectedWeekdays.map((weekday) => weekday.day === selectedDay ? {...weekday, selected: !weekday.selected} : weekday)
        setSelectedWeekdays(updatedSelectedWeekdays)
    }

    const convertedEndDate = formatDate(updatedSchedule.end_date, DateFormats.DOT_DMY)

    const initialFormValues = {
        team_member: isEdit ? updatedSchedule?.member : null,
        months: selectedMonths,
        start_date: isEdit ? formatDate(updatedSchedule.start_date, DateFormats.DOT_DMY) : currentDate,
        start_time: isEdit ? formatTimeString(isTimeRangeExceeded(updatedSchedule?.end_time, updatedSchedule?.start_time) ? DEFAULT_START_HOUR : updatedSchedule?.start_time, DEFAULT_SECOND) : '',
        end_time: isEdit ? formatTimeString(isTimeRangeExceeded(updatedSchedule?.end_time, updatedSchedule?.start_time) ? DEFAULT_END_HOUR : updatedSchedule?.end_time, DEFAULT_SECOND) : '',
        end_date: isEdit ? (convertedEndDate === currentEndDate ? currentDate : convertedEndDate) : currentDate
    }

    const renderWeekdayLabel = (weekday, index, errors, setFieldError) => (
        <label key={index} className={classNames({
            'weekday-button': true,
            'selected': isOnCallEnabled && weekday.selected,
            'error': errors.weekdays,
            'disabled': !isOnCallEnabled
        })}>
            <input
                type="checkbox"
                checked={isOnCallEnabled && weekday.selected}
                disabled={!isOnCallEnabled}
                value={weekday}
                name={names.weekdays}
                onChange={() => handleWeekdays(weekday.day, setFieldError)}
            />
            {weekday.day}
        </label>
    )

    const handleSubmit = async(values, setFieldError) => {
        const startTime = formatTimeString(getFormattedTimeInTimeZone(values.start_time, false), DEFAULT_SECOND)
        const endTime = formatTimeString(getFormattedTimeInTimeZone(values.end_time, false), DEFAULT_SECOND)
        const endDate = combine(dateStatus === NEVER ? currentEndDate : values.end_date, endTime, DateFormats.DOT_DMY)
        const startDate = combine(values.start_date, startTime, DateFormats.DOT_DMY)
        setIsButtonLoading(true)

        const scheduleData = {
            team_member_id: values.team_member.id,
            start_date: startDate,
            end_date: endDate,
            months_of_year: values.months.map(month => month.index),
            weeks_of_month: selectedWeeks.filter(week => week.selected).map(week => week.value === 'L' ? -1 : week.value),
            week_days: selectedWeekdays.filter(weekday => weekday.selected).map(weekday => weekday.value),
            start_time: startTime,
            end_time: endTime
        }

        isEdit ? await dispatch(updateSchedule(currentTeamId, {...scheduleData, created_at: updatedSchedule.created_at}, setFieldError)) :
            await dispatch(createSchedule(currentTeamId, scheduleData, setFieldError))

        setIsButtonLoading(false)
        await dispatch(getAllMembersSchedule(members))
    }

    useEffect(() => {
        const updatedSelectedWeekdays = text.weekdays.map((weekday, index) => ({
            day: weekday.label,
            value: index,
            selected: true
        }))

        const updatedSelectedWeeks = Array.from({length: 6}).map((_, i) => {
            return i === 5 ? {value: 'L', selected: true} :  {
                value: i + 1,
                selected: true
            }
        })

        setDaysRange(ALL)
        setSelectedWeekdays(updatedSelectedWeekdays)
        setSelectedWeeks(updatedSelectedWeeks)
    }, [])

    useEffect(() => {
        formikRef.current?.setFieldValue(names.weekdays, selectedWeekdays.filter(weekday => weekday.selected).map(weekday => weekday.value))
    }, [formikRef.current])

    useEffect(() => {
        if (isEdit) {
            setSelectedWeekdays(text.weekdays.map((weekday, index) => ({day: weekday.label, value: index, selected: updatedSchedule?.week_days?.includes(index)})))
            setDaysRange(text.weekdays?.every((weekday, index) => updatedSchedule.week_days.includes(index)) ? ALL : monFriDays?.every(index => updatedSchedule.week_days.includes(index)) ? MON_FRI : '')
            setSelectedWeeks(Array.from({length: 6}).map((_, i) => (i === 5 ? {value: 'L',
                selected: updatedSchedule?.weeks_of_month?.includes(-1)} : {
                value: i + 1,
                selected: updatedSchedule?.weeks_of_month?.includes(i + 1)
            })))
            setSelectedMonths(text.months.map(month => ({...month, isChecked: updatedSchedule?.months_of_year?.includes(month.index)})))
            setDateStatus(convertedEndDate === currentEndDate ? NEVER : ON)
        }
    }, [isEdit])

    return (
        <>
            <Formik initialValues={initialFormValues}
                onSubmit={handleSubmit}
                innerRef={formikRef}
                validationSchema={scheduleSchema}
                validateOnBlur={false}
                validateOnChange={false}>
                {({errors, handleSubmit, setFieldValue, values, setFieldError}) =>  {
                    return (
                        <form onSubmit={handleSubmit} className={classNames('schedule-form', {
                            dark: isDarkMode
                        })}>
                            <div className={classNames('schedule-form-field', {
                                dark: isDarkMode
                            })} ref={containerRef}>
                                <span className='field-label'>{text.team_members}</span>
                                <CustomSelect
                                    name={names.team_member}
                                    onChange={(name, data) => {
                                        setFieldValue(name, data)
                                    }}
                                    value={values.team_member}
                                    isCheckboxDisabled={true}
                                    placeholder={text.choose_members}
                                    infoText={text.team_members_info}
                                    options={membersOptions}
                                    infoIconSize={24}
                                    hasIcon
                                    isDisabled={!isOnCallEnabled}
                                    activeInfo={active === names.team_member}
                                    handleInfo={(state) => handleInfo(names.team_member, state)}
                                    error={errors.team_member}/>
                            </div>
                            <div className='schedule-form-field'>
                                <span className='field-label'>{text.start_time}</span>
                                <CustomTimePicker
                                    placeholder={text.start_time_placeholder}
                                    value={values.start_time}
                                    onChange={(value) => setFieldValue(names.start_time, formatTimeString(value, DEFAULT_SECOND))}
                                    infoText={text.start_time_info}
                                    isDisabled={!isOnCallEnabled}
                                    activeInfo={active === names.start_time}
                                    handleInfo={(state) => handleInfo(names.start_time, state)}
                                    error={errors.start_time}
                                    isFocused={!isEdit}
                                />
                            </div>
                            <div className='schedule-form-field'>
                                <span className='field-label'>{text.end_time}</span>
                                <CustomTimePicker
                                    placeholder={text.end_time_placeholder}
                                    value={values.end_time}
                                    onChange={(value) => setFieldValue(names.end_time, formatTimeString(value, DEFAULT_SECOND))}
                                    infoText={text.end_time_info}
                                    isDisabled={!isOnCallEnabled}
                                    activeInfo={active === names.start_time}
                                    handleInfo={(state) => handleInfo(names.end_time, state)}
                                    error={errors.end_time}
                                    isFocused={!isEdit}
                                />
                            </div>
                            <div className='schedule-form-field flex-column'>
                                <span className='field-label'>{text.weekdays_label}</span>
                                <div className='weekdays'>
                                    <div className='weekdays-checkboxes'>
                                        <Checkbox
                                            label={text.mon_fri}
                                            name={names.weekdays_range}
                                            type="checkbox"
                                            value={daysRange}
                                            disabled={!isOnCallEnabled}
                                            onChange={() => handleWeekdays(MON_FRI, setFieldError)}
                                            checked={daysRange === MON_FRI}
                                        />
                                        <DividerIcon/>
                                        <Checkbox
                                            label={text.all}
                                            name={names.weekdays_range}
                                            type="checkbox"
                                            value={daysRange}
                                            disabled={!isOnCallEnabled}
                                            onChange={() => handleWeekdays(ALL, setFieldError)}
                                            checked={daysRange === ALL}
                                        />
                                    </div>
                                    <div className='weekdays-container'>
                                        <span className={classNames('weekdays-buttons', {
                                            disabled: !isOnCallEnabled,
                                            dark: isDarkMode
                                        })}>
                                            {selectedWeekdays?.map((weekday, index) => (
                                                daysRange === MON_FRI ? weekday.selected && renderWeekdayLabel(weekday, index, errors, setFieldError) :
                                                    renderWeekdayLabel(weekday, index, errors, setFieldError)
                                            ))}
                                        </span>
                                        <Tooltip
                                            placement="left"
                                            trigger="click"
                                            content={text.weekdays_info}
                                            interactive={false}
                                            appendTo={containerRef?.current}
                                        >
                                            <HelpIcon stroke={colors.colorBgFilter} width="24" height="24"/>
                                        </Tooltip>
                                    </div>

                                </div>
                            </div>
                            <div className='advanced-section'>
                                <span className='trigger-advanced' onClick={triggerAdvanced}>
                                    {isAdvanceShowed ? <span className='advanced-info'>{text.hide_advanced}  <ArrowUpIcon width={12} height={10} stroke={colors.colorLightBlue}/></span> :
                                        <span className='advanced-info'>{text.show_advanced}  <ArrowDownIcon width={12} height={10} stroke={colors.colorLightBlue}/></span>}
                                </span>
                                {isAdvanceShowed &&
                        <div className={classNames('advanced-container schedule-form', {
                            dark: isDarkMode
                        })}>
                            <span className='advanced-title'>{text.advanced_options}</span>
                            <div className='schedule-form-field'>
                                <span className='field-label'>{text.choose_months}</span>
                                <CustomSelect
                                    name={names.months}
                                    onChange={(name, data) => {
                                        setFieldValue(name, data)
                                    }}
                                    isMulti
                                    placeholderAsLabel={false}
                                    value={values.months}
                                    isAllSelected
                                    placeholder={text.choose_months}
                                    infoText={text.months_info}
                                    options={selectedMonths}
                                    isDisabled={!isOnCallEnabled}
                                    infoIconSize={24}
                                    activeInfo={active === names.months}
                                    handleInfo={(state) => handleInfo(names.months, state)}
                                    error={errors.months}/>
                            </div>
                            <div className='schedule-form-field'>
                                <span className='field-label'>{text.choose_weeks}</span>
                                <div className='weekdays'>
                                    <div className='weekdays-container'>
                                        <span className={classNames('weekdays-buttons', {
                                            disabled: !isOnCallEnabled,
                                            dark: isDarkMode
                                        })}>
                                            {selectedWeeks?.map((week, index) => (
                                                <label key={index} className={classNames({
                                                    'weekday-button': true,
                                                    'selected': week.selected,
                                                    'disabled': !isOnCallEnabled
                                                })}>
                                                    <input
                                                        type="checkbox"
                                                        checked={week.selected}
                                                        onChange={() => handleWeeks(week.value)}
                                                        disabled={!isOnCallEnabled}
                                                    />
                                                    {week.value}
                                                </label>
                                            ))}
                                        </span>
                                        <Tooltip
                                            placement="left"
                                            trigger="click"
                                            content={text.weeks_info}
                                            appendTo={containerRef?.current}
                                        >
                                            <HelpIcon stroke={colors.colorBgFilter} width="24" height="24"/>
                                        </Tooltip>
                                    </div>

                                </div>
                            </div>
                            <div className='schedule-form-field'>
                                <span className='field-label'>{text.start_date}</span>
                                <CustomCalendar
                                    placeholder={text.start_date_placeholder}
                                    value={values.start_date}
                                    name={names.start_date}
                                    infoText={text.start_date_info}
                                    currentDate={normalize(values.start_date, DateFormats.DOT_DMY)}
                                    activeInfo={active === names.start_date}
                                    handleInfo={(state) => handleInfo(names.start_date, state)}
                                    error={errors.start_date}
                                    isDisabled={!isOnCallEnabled}
                                    onChange={(date) => setFieldValue(names.start_date, formatDate(date, DateFormats.DOT_DMY))}
                                />
                            </div>
                            <div className='schedule-form-field flex-column align-items-start'>
                                <span className='field-label'>{text.end_date}</span>
                                <div className='weekdays-container'>
                                    <div className='weekdays-checkboxes'>
                                        <Checkbox
                                            label={text.never}
                                            name={names.end_date_available}
                                            type="checkbox"
                                            disabled={!isOnCallEnabled}
                                            value={dateStatus}
                                            onChange={() => setDateStatus(NEVER)}
                                            checked={dateStatus === NEVER}
                                        />
                                        <DividerIcon/>
                                        <Checkbox
                                            label={text.on}
                                            name={names.end_date_available}
                                            type="checkbox"
                                            disabled={!isOnCallEnabled}
                                            value={dateStatus}
                                            onChange={() => setDateStatus(ON)}
                                            checked={dateStatus === ON}
                                        />
                                    </div>
                                    <CustomCalendar
                                        placeholder={text.end_date_placeholder}
                                        value={values.end_date}
                                        infoText={text.end_date_info}
                                        name={names.end_date}
                                        currentDate={normalize(values.end_date, DateFormats.DOT_DMY)}
                                        activeInfo={active === names.end_date}
                                        handleInfo={(state) => handleInfo(names.end_date, state)}
                                        onChange={(date) => setFieldValue(names.end_date, formatDate(date, DateFormats.DOT_DMY))}
                                        isDisabled={dateStatus === NEVER || !isOnCallEnabled}
                                        error={errors.end_date}
                                    />
                                </div>
                            </div>
                        </div>}
                            </div>
                            <div className='d-flex schedule-form-buttons'>
                                <SimpleBlueButton className='button cancel-button' type="cancel" onClick={() => handleModal(hasBackOption ? 'view' : null)}>
                                    {hasBackOption ? text.back : text.cancel_btn}
                                </SimpleBlueButton>
                                <SimpleBlueButton className='button button-save' type="submit"
                                    disabled={isButtonLoading || !isOnCallEnabled}>
                                    {isButtonLoading && <ButtonLoader/>}
                                    {text.save_btn}
                                </SimpleBlueButton>
                            </div>
                        </form>
                    )
                }}
            </Formik>
        </>)
}

export default CreateEditSchedule
